<template>
  <div
    v-loading="isLoading"
    class="RequestRow grid grid-cols-12 gap-x-4 gap-y-2"
    :class="{
      'RequestRow--emergency': props.request.type_id === $store.state.requests.EMERGENCY_TYPE_ID,
      'RequestRow--organization-btn': props.request.organization && props.request?.client?.is_temp && !props.request?.is_from_qr,
      'serviceA': props.request.service_id === 1,
      'serviceB': props.request.service_id === 2,
      'shadow-pink': !props.quickPreview,
    }"
  >
    <div
      class="col-span-12 RequestDesc mb-4 md:mb-0"
      :class="props.quickPreview ? 'md:col-span-5' : 'md:col-span-4'"
    >
      <ServiceIconXl
        :service-id="props.request.service_id"
        class="mr-4"
      />
      <div>
        <div class="Label flex items-center">
          <OperatorRequestRowClient
            v-if="props.type !== 'history'"
            :client="props.request.client"
            :request="props.request"
          />
          <OperatorRequestRowType
            v-else-if="props.request.service_id === 1"
            :type-id="props.request.type_id"
            :client="props.request.client"
          />
          <OperatorRequestRowTranscriptionType
            v-else-if="props.request.service_id === 2 && props.request.transcription_type_id"
            :transcription-type-id="props.request.transcription_type_id"
            :client="props.request.client"
            hide-icon
          />
          <strong
            v-else-if="props.type === 'history' && props.request.service_id === 2 && !props.request.transcription_type_id"
          >
            Přepis
          </strong>

          <DeviceIcon
            :type="props.request.device"
            class="color-muted ml-2"
          />
          <OperatorRequestQRButtonBadge
            :request="props.request"
            class="ml-2"
          />
          <el-tooltip
            v-if="props.request.bad_connection"
            content="Pomalé internetové připojení"
            placement="top"
          >
            <icon-bad-connection class="text-danger-500 ml-2" />
          </el-tooltip>
          <OperatorRequestDeletedBadge
            :request="props.request"
            class="ml-2"
          />
        </div>
        <div
          class="RequestRowCreatedAt text-sm text-text-light"
          data-testid="created-at"
        >
          {{ dayjs(props.request.created_at).format('l, LT') }}
        </div>
      </div>
    </div>
    <div
      class="col-span-12 grid grid-cols-12 px-5 md:px-0"
      :class="`${props.quickPreview ? 'md:col-span-7' : 'md:col-span-8'}`"
    >
      <div
        v-if="props.type !== 'history'"
        :class="`col-span-6 ${props.type === 'history-all' ? 'md:col-span-3' : 'md:col-span-4'}`"
      >
        <OperatorRequestRowType
          v-if="props.request.service_id === 1"
          :type-id="props.request.type_id"
          :client="props.request.client"
        />
        <OperatorRequestRowTranscriptionType
          v-if="props.request.service_id === 2"
          :transcription-type-id="props.request.transcription_type_id"
          :client="props.request.client"
        />
      </div>
      <div
        class="col-span-6 flex flex-col justify-center"
        :class="props.type === 'history' ? 'md:col-span-5' : (props.quickPreview ? '' : 'md:col-span-4')"
        data-testid="state-and-book-time"
      >
        <template v-if="props.request.state_id === 1">
          <template v-if="props.request.has_book_time">
            <template v-if="dayjs(now).isBefore(props.request.book_time)">
              <div class="Label font-semibold text-primary-500">
                Rezervace
              </div>
              <div class="text-text-light text-sm">
                {{ dayjs(props.request.book_time).format('l, LT') }}
              </div>
            </template>
            <template v-else>
              <div
                class="Label font-semibold text-danger-500"
              >
                Čeká
                <strong class="font-semibold">
                  {{ timeDifference(now, props.request.book_time, 'minute', true) }} min
                </strong>
              </div>
              <div
                class="text-text-light text-sm"
              >
                Rezervace
                {{ dayjs(props.request.book_time).format('l, LT') }}
              </div>
            </template>
          </template>

          <template v-else>
            <div
              class="Label"
              :class="{ 'text-danger-500': props.request.type_id === $store.state.requests.EMERGENCY_TYPE_ID }"
            >
              Čeká
              <strong class="font-semibold">
                {{ timeDifference(now, props.request.book_time, 'minute', true) }} min
              </strong>
            </div>
            <div
              v-if="props.request.type_id !== $store.state.requests.EMERGENCY_TYPE_ID"
              class="text-text-light text-sm"
            >
              bez rezervace
            </div>
          </template>
        </template>

        <div
          v-if="props.request.state_id === 4 || props.request.state_id === 5"
          class="flex flex-col mr-4"
        >
          <strong
            v-if="props.request.state_id === 4"
            class="mb-1"
          >
            Uzavřeno
          </strong>
          <strong
            v-else-if="props.request.state_id === 5"
            class="text-danger-500 mb-1"
          >
            Zrušeno
          </strong>
          <span
            v-if="props.request.finalized_at"
            class="text-text-light"
          >
            {{ dayjs(props.request.finalized_at).format('DD. MM. YYYY, HH:mm') }}
          </span>
        </div>
      </div>
      <div
        class="requestActions col-span-12 flex justify-center md:justify-end items-center mt-4 md:mt-0 md:mb-0 md:pr-5"
        :class="props.quickPreview ? 'md:col-span-2' : (props.type === 'history' ? 'md:col-span-7' : (props.type === 'history-all' ? 'md:col-span-5' : 'md:col-span-4'))"
        data-testid="actions"
      >
        <div
          v-if="props.request.state_id !== 1 && props.request.operator"
          class="flex flex-col mr-auto ml-0"
        >
          <strong
            v-if="(props.request.state_id === 2 || props.request.state_id === 3) && (props.request.operator.id !== useAuthStore()?.user?.id)"
            class="text-primary-500 mb-1"
          >
            Vyřizuje
          </strong>
          <strong
            v-if="props.request.state_id === 4 || props.request.state_id === 5"
            class="mb-1"
          >
            Vyřídil
          </strong>
          <span
            v-if="(props.request.operator.id !== useAuthStore()?.user?.id || (props.request.state_id === 4 || props.request.state_id === 5))"
            class="text-text-light"
          >
            {{ props.request.operator.first_name }} {{ props.request.operator.last_name }}
          </span>
        </div>

        <template v-if="!props.quickPreview">
          <template v-if="props.request.operator && props.request.operator.id === useAuthStore()?.user?.id">
            <el-tooltip
              v-if="props.request.state_id === 2 || props.request.state_id === 3"
              content="Uzavřít požadavek"
              placement="top"
            >
              <el-button
                type="primary"
                class="ml-4"
                plain
                circle
                data-testid="finish-request"
                @click="updateRequestState(props.request.id, 4)"
              >
                <icon-flag class="text-primary-500" />
              </el-button>
            </el-tooltip>
            <el-tooltip
              v-if="props.request.state_id === 3"
              content="Pokračovat"
              placement="top"
            >
              <el-button
                type="primary"
                class="ml-4"
                plain
                circle
                @click="updateRequestState(props.request.id, 2)"
              >
                <icon-play class="text-primary-500" />
              </el-button>
            </el-tooltip>
            <el-tooltip
              v-if="props.request.state_id === 2 || props.request.state_id === 3"
              content="Předat požadavek"
              placement="top"
            >
              <el-button
                class="ml-4"
                plain
                circle
                data-testid="forward-request"
                @click="useDialogStore().update({ dialogName: 'requestForward', data: { visible: true, id: props.request.id, service_id: props.request.service_id } })"
              >
                <icon-transfer class="h-auto" />
              </el-button>
            </el-tooltip>
          </template>

          <el-tooltip
            v-if="props.request.state_id === 1 && props.request.client?.lang === useAuthStore()?.user?.lang"
            content="Přijmout požadavek"
            placement="top"
          >
            <el-button
              type="primary"
              class="ml-4"
              plain
              circle
              data-testid="accept-request"
              @click="acceptRequest"
            >
              <icon-check class="text-primary-500 h-auto" />
            </el-button>
          </el-tooltip>
          <!-- State 5 alongside 4 so a cancelled request stays reachable: it is the
               state abuse ends up in, and the abuse report has 48 hours to be filed -->
          <template v-if="!props.request.operator || props.request.operator.id === useAuthStore()?.user?.id || props.request.state_id === 4 || props.request.state_id === 5">
            <el-tooltip
              content="Chat s klientem"
              placement="top"
            >
              <nuxt-link
                :to="`/operator/requests/${props.request.id}/chat`"
                class="ml-4"
                data-testid="open-chat"
              >
                <el-button
                  type="primary"
                  plain
                  circle
                >
                  <icon-chat class="color-muted" />
                </el-button>
              </nuxt-link>
            </el-tooltip>
          </template>
        </template>
        <el-tooltip
          :content="props.quickPreview ? 'Otevřít v nové kartě' : 'Zobrazit detail'"
          placement="top"
        >
          <nuxt-link
            :to="`/operator/requests/${props.request.id}/detail`"
            :target="props.quickPreview ? '_blank' : undefined"
            class="ml-4"
            data-testid="open-detail"
          >
            <el-button
              type="primary"
              plain
              circle
            >
              <component
                :is="props.quickPreview ? 'icon-leave' : 'icon-right'"
                class="color-muted"
              />
            </el-button>
          </nuxt-link>
        </el-tooltip>
      </div>
    </div>
  </div>
</template>

<script setup>
const props = defineProps({
  request: {
    type: Object,
    required: true,
  },
  type: {
    type: String,
    default: null,
  },
  quickPreview: {
    type: Boolean,
    default: false,
  },
});

// Refs
const { isLoading, updateRequestState } = useRequestState();
const now = useNow();

// Methods
const acceptRequest = () => {
  if (props.request?.client?.lang === 'ua' && useAuthStore()?.user?.lang !== 'ua') {
    useDialogStore().open('confirmRequestAccept');
    useDialogStore().update({
      dialogName: 'confirmRequestAccept',
      data: {
        title: 'Ukrajinský požadavek',
        text: 'Opravdu chcete přijmout ukrajinský požadavek?',
        acceptText: 'Přijmout požadavek',
        accept: () => {
          useDialogStore().close('confirmRequestAccept');
          updateRequestState(props.request.id, 2);
        },
      },
    });
  } else if (props.request.has_book_time) {
    useDialogStore().open('confirmRequestAccept');
    useDialogStore().update({
      dialogName: 'confirmRequestAccept',
      data: {
        title: 'Požadavek s rezervací',
        text: 'Opravdu chcete přijmout požadavek s rezervací?',
        acceptText: 'Přijmout požadavek',
        accept: () => {
          useDialogStore().close('confirmRequestAccept');
          updateRequestState(props.request.id, 2);
        },
      },
    });
  } else {
    updateRequestState(props.request.id, 2);
  }
};
</script>

<style scoped>
@reference "assets/css/tailwind.css";

.RequestRow {
  display: flex;
  align-items: center;
  margin: 0;
  margin-bottom: 8px;
  border-radius: 2px;
  padding-top: 20px;
  padding-bottom: 20px;

  @media (max-width: theme(--breakpoint-sm)) {
    padding-top: 10px;
    padding-bottom: 10px;
  }

  @apply bg-white;

  &.RequestRow--emergency {
    border-left: 4px solid var(--color-danger-500);
  }

  &.RequestRow--organization-btn {
    &.serviceA {
      border-left: 4px solid var(--color-secondary-500);
    }

    &.serviceB {
      border-left: 4px solid var(--color-tertiary-500);
    }
  }

  .Label {
    &:not(:only-child) {
      margin-bottom: 2px;
    }
  }

  .RequestDesc {
    display: flex;
    align-items: center;
    padding-left: 20px;
    padding-right: 5px;
  }
}
</style>
